import { useEffect, useState } from "react";
import { getDefaultEntity } from "../../../../utils/helpers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import BaseCard from "../../../../components/ui/BaseCard";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseLoader from "../../../../components/ui/BaseLoader";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseWrapper from "../../../../components/ui/BaseWrapper";
import { getClientById, updateOwnCustomerProfile } from "../../../../services/api/client";
import { showToast } from "../../../../store/toast/toastSlice";

const validationSchema = Yup.object({
  accountsPerson: Yup.string().required("Accounts Person is required"),
  accountsEmail: Yup.string()
    .required("Accounts Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  accountsPhone: Yup.string().required("Accounts Phone is required"),
  taxRegistrationNumber: Yup.string().notRequired(),
});

const REQUIRED_FIELDS = [
  ["accountsPerson", "Accounts Person"],
  ["accountsEmail", "Accounts Email"],
  ["accountsPhone", "Accounts Phone"],
];

const missingFieldsOf = (values) =>
  REQUIRED_FIELDS.filter(([key]) => !String(values?.[key] ?? "").trim()).map(([, label]) => label);

const CustomerAccountStep = ({ customerId, onDirtyChange = null, onCompletionChange = null }) => {
  const dispatch = useDispatch();
  const [isEditable, setIsEditable] = useState(false);

  const {
    data: clientData,
    isPending: isClientLoading,
    refetch: refetchClient,
  } = useQuery({
    queryKey: ["client", String(customerId)],
    queryFn: () => getClientById(customerId),
    enabled: !!customerId,
  });

  const { isPending: isSaving, mutateAsync: saveProfile } = useMutation({
    mutationFn: updateOwnCustomerProfile,
  });

  const customer = clientData?.data;
  const entity = getDefaultEntity(customer);
  const account = entity?.clientAccount;
  // Nothing here comes from signup, so an untouched account opens ready to fill in.
  const hasData = !!(account?.accountsPerson || account?.email || account?.phone);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      accountsPerson: account?.accountsPerson || "",
      accountsEmail: account?.email || "",
      accountsPhone: account?.phone || "",
      taxRegistrationNumber: entity?.taxRegistrationNumber || "",
    },
    validationSchema,
    onSubmit: (values) => handleSave(values),
  });

  const handleSave = async (values) => {
    try {
      const formData = new FormData();
      formData.append("accountsPerson", values.accountsPerson);
      formData.append("accountsEmail", values.accountsEmail);
      formData.append("accountsPhone", values.accountsPhone);
      formData.append("taxRegistrationNumber", values.taxRegistrationNumber || "");

      await saveProfile(formData);
      dispatch(showToast({ message: "Company updated successfully", type: "success" }));
      await refetchClient();
      setIsEditable(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to update company",
          type: "error",
        }),
      );
    }
  };

  useEffect(() => {
    if (!customer) return;
    onCompletionChange?.(
      missingFieldsOf({
        accountsPerson: account?.accountsPerson,
        accountsEmail: account?.email,
        accountsPhone: account?.phone,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    onDirtyChange?.(formik.dirty);
  }, [formik.dirty, onDirtyChange]);

  useEffect(() => {
    if (customer && !hasData) {
      setIsEditable(true);
    }
  }, [customer, hasData]);

  if (!customerId) {
    return <p className="py-4">No customer record is linked to this account.</p>;
  }

  if (isClientLoading) {
    return (
      <div className="flex justify-center py-10">
        <BaseLoader isLoading={isClientLoading} />
      </div>
    );
  }

  const isDisabled = !isEditable && hasData;

  return (
    <div>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg bg-transparent">
        <div className="flex justify-between w-full">
          <div className="hidden sm:block w-2/6"></div>
          <div className="sm:col-span-4 col-span-7 flex items-center justify-end gap-3">
            {!isEditable && hasData ? (
              <BaseButton
                variant="contained"
                color="primary"
                onClick={() => setIsEditable(true)}
                className="py-[5px] whitespace-nowrap"
              >
                Edit Accounts Details
              </BaseButton>
            ) : (
              <>
                {formik.dirty && (
                  <p className="text-amber-600 text-sm font-normal self-center">
                    You have unsaved changes — click Update to apply.
                  </p>
                )}
                {hasData && (
                  <BaseButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      formik.resetForm();
                      setIsEditable(false);
                    }}
                    className="py-[5px]"
                  >
                    Cancel
                  </BaseButton>
                )}
                <BaseButton
                  loaderSize="24px"
                  isLoading={isSaving}
                  disabled={isSaving || !formik.dirty}
                  variant="contained"
                  color="primary"
                  onClick={formik.handleSubmit}
                  className="py-[5px]"
                >
                  Update
                </BaseButton>
              </>
            )}
          </div>
        </div>
      </BaseCard>

      <BaseWrapper>
        <div className="pb-8">
          {/* Text fields, the select and the phone input each size themselves
              differently, so the row pins them all to one height. */}
          <div className="grid grid-cols-3 gap-y-5 mt-2 text-black [&_.MuiInputBase-root]:h-[46px]">
            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                fullHeight={false}
                fullWidth
                labelShrink={false}
                id="accountsPerson"
                name="accountsPerson"
                label="Accounts Person"
                requirement="required"
                isDefaultField={false}
                value={formik.values.accountsPerson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountsPerson && formik.errors.accountsPerson}
                helperText={formik.touched.accountsPerson && formik.errors.accountsPerson}
                disabled={isDisabled}
              />
            </div>

            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                fullHeight={false}
                fullWidth
                labelShrink={false}
                id="accountsEmail"
                name="accountsEmail"
                label="Accounts Email"
                requirement="required"
                isDefaultField={false}
                value={formik.values.accountsEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountsEmail && formik.errors.accountsEmail}
                helperText={formik.touched.accountsEmail && formik.errors.accountsEmail}
                disabled={isDisabled}
              />
            </div>

            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              {/* Same markup as the client form's Phone field, so the two line up. */}
              <label
                htmlFor="accountsPhone"
                className="flex items-baseline min-h-[26px]  text-[13px] font-normal text-[#686868]"
              >
                Accounts Phone
                <span className="text-red-500 ml-0.5" aria-hidden="true">
                  *
                </span>
              </label>

              <div className="relative w-full">
                <PhoneInput
                  inputClass={`!w-full !h-[46px] !pl-12 !pr-3 !text-sm !font-light
                !text-black !border !rounded-md !shadow-md
                ${isDisabled ? "!bg-[#F6F6F6] !cursor-not-allowed !text-gray-500" : "!bg-white"}
                ${formik.touched.accountsPhone && formik.errors.accountsPhone && "!border-gray-300"}
                focus:!outline-none focus:!ring-1 focus:!ring-orange-400`}
                  buttonClass="!border-none !bg-transparent"
                  containerClass="!w-full"
                  specialLabel=""
                  country={"au"}
                  value={formik.values.accountsPhone}
                  onChange={(value) => formik.setFieldValue("accountsPhone", value)}
                  onBlur={() => formik.setFieldTouched("accountsPhone", true)}
                  inputProps={{ name: "accountsPhone", id: "accountsPhone", type: "tel" }}
                  disabled={isDisabled}
                />

                {formik.touched.accountsPhone && formik.errors.accountsPhone && (
                  <FormHelperText error>{formik.errors.accountsPhone}</FormHelperText>
                )}
              </div>
            </div>

            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                fullHeight={false}
                fullWidth
                labelShrink={false}
                id="taxRegistrationNumber"
                name="taxRegistrationNumber"
                label="VAT/GST Registration"
                value={formik.values.taxRegistrationNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isDefaultField={false}
                error={formik.touched.taxRegistrationNumber && formik.errors.taxRegistrationNumber}
                helperText={
                  formik.touched.taxRegistrationNumber && formik.errors.taxRegistrationNumber
                }
                disabled={isDisabled}
              />
            </div>
          </div>
        </div>
      </BaseWrapper>
    </div>
  );
};

export default CustomerAccountStep;
